import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Card, Spinner, Alert } from "@/components/primitives";
import type { ScorecardData, StartResult } from "@/lib/types";

// Scorecard (Jinja: scorecard.html). Scores are parsed SERVER-SIDE (api_candidate
// ._parse_scorecard) so React just draws the radar + bars from `dimensions`, and
// renders the server-sanitised markdown body. Generation + polling mirror the
// Jinja inline script (409-retry while the session finalises, 3s poll, 180s cap).

const POLL_MS = 3000;
const MAX_POLLS = 60; // 180s

function barColour(pct: number) {
  if (pct >= 75) return "var(--good)";
  if (pct >= 50) return "var(--accent)";
  return "var(--warn)";
}


export function Scorecard() {
  const { token, sessionId } = useParams<{ token: string; sessionId: string }>();
  const navigate = useNavigate();
  const { data: invite } = useInvite(token);

  const [sc, setSc] = useState<ScorecardData | null>(null);
  const [phase, setPhase] = useState<"loading" | "scoring" | "ready" | "failed" | "nocontent">("loading");
  const polls = useRef(0);
  const genKicked = useRef(false);

  // "Hear your summary" is paused for this version (2026-09-02). The audio
  // state, the playAudio handler and the button were removed together; the
  // server side is untouched, so restoring them is the whole change. See the
  // note in service/scorecard.py:_post_session_sync.

  // Shared response handler for both the initial loop and regenerate. Returns
  // true when a terminal state was reached (ready / failed / timed out) so
  // callers know whether to schedule another poll tick.
  const applyResponse = useCallback((r: ScorecardData | ReturnType<typeof Object>): boolean => {
    const d = r as ScorecardData;
    if (d.ok && d.ready) { setSc(d); setPhase("ready"); return true; }
    if (d.ok && d.failed) { setSc(d); setPhase("failed"); return true; }
    polls.current += 1;
    if (polls.current >= MAX_POLLS) { setPhase("failed"); return true; }
    return false;
  }, [setSc, setPhase]);

  useEffect(() => {
    if (!sessionId) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;

    async function loop() {
      if (!live || !sessionId) return;

      if (!genKicked.current) {
        // Check for an existing ready scorecard before triggering generation.
        // This prevents re-generating a scorecard every time the page is opened.
        const existing = await candidateApi.getScorecard(sessionId);
        if (!live) return;
        if (existing.ok && (existing as ScorecardData).ready) {
          setSc(existing as ScorecardData);
          setPhase("ready");
          return;
        }
        // No ready scorecard — kick generation. Retry on 409 (session still finalising).
        const gen = await candidateApi.genScorecard(sessionId);
        if (!live) return;
        if (!gen.ok && gen.retry) {
          timer = setTimeout(loop, POLL_MS);
          return;
        }
        if (!gen.ok) {
          setPhase((gen as { error?: string }).error === "no_content" ? "nocontent" : "failed");
          return;
        }
        genKicked.current = true;
      }

      setPhase("scoring");
      const r = await candidateApi.getScorecard(sessionId);
      if (!live) return;
      if (!applyResponse(r)) timer = setTimeout(loop, POLL_MS);
    }

    loop();
    return () => { live = false; clearTimeout(timer); };
  }, [sessionId, applyResponse]);

  async function practiseAgain() {
    const form = new FormData();
    const r = await candidateApi.start(token!, form);
    if (r.ok) {
      const sid = (r as StartResult).sessionId;
      if (sid) { navigate(`/invite/${token}/notify/${sid}`); return; }
    }
    if ((r as { field?: string }).field === "cv") {
      navigate(`/invite/${token}/setup`);
      return;
    }
    navigate(`/invite/${token}/setup`);
  }

  async function regenerate() {
    if (!sessionId) return;
    genKicked.current = false;
    polls.current = 0;
    setPhase("loading");
    const gen = await candidateApi.genScorecard(sessionId);
    if (!gen.ok) {
      setPhase((gen as { error?: string }).error === "no_content" ? "nocontent" : "failed");
      return;
    }
    setPhase("scoring");
    const tick = async () => {
      const r = await candidateApi.getScorecard(sessionId);
      if (!applyResponse(r)) setTimeout(tick, POLL_MS);
    };
    setTimeout(tick, POLL_MS);
  }

  const agencyProps = {
    agencyName: invite?.agencyName,
    logoUrl: invite?.branding?.logoUrlLight || undefined,
    logoFilter: invite?.branding?.logoFilter,
  };

  if (phase === "loading" || phase === "scoring") {
    return (
      <CandidateFlow {...agencyProps}>
        <Card className="flex flex-col items-center gap-4 py-10 text-center">
          <Spinner size={28} />
          <p className="text-sm text-slate-500">Generating scorecard. This takes about 60 seconds…</p>
        </Card>
      </CandidateFlow>
    );
  }

  if (phase === "nocontent") {
    return (
      <CandidateFlow {...agencyProps}>
        <Card className="space-y-4">
          <Alert variant="warn">
            This session ended before any of your answers were recorded, so there is nothing to
            score yet. Start a practice session and speak your answers to get a scorecard.
          </Alert>
          <Button variant="primary" className="w-full" onClick={practiseAgain}>Practise again →</Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate(`/invite/${token}/home`)}>Back to home</Button>
        </Card>
      </CandidateFlow>
    );
  }

  if (phase === "failed") {
    return (
      <CandidateFlow {...agencyProps}>
        <Card className="space-y-4">
          <Alert variant="warn">
            Scorecard generation failed{sc?.prepError ? `: ${sc.prepError}` : ""}. Click Regenerate to try again.
          </Alert>
          <Button variant="primary" className="w-full" onClick={practiseAgain}>Practise again →</Button>
          <Button variant="accent" className="w-full" onClick={regenerate}>Regenerate scorecard</Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate(`/invite/${token}/home`)}>Back to home</Button>
        </Card>
      </CandidateFlow>
    );
  }

  // ready
  const meta = sc?.session;
  return (
    <CandidateFlow {...agencyProps} width="wide">
      {/* Gradient header band */}
      <div style={{ background: "linear-gradient(155deg,#EFEBFF 0%,#F5E6F7 55%,#FCEFEA 100%)", borderRadius: 20, padding: "28px 28px 24px", marginBottom: 20 }}>
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] mb-2" style={{ color: "var(--accent)" }}>
          {meta?.role}{meta?.company ? ` · ${meta.company}` : ""}
        </p>
        {sc?.overall != null && (
          <div className="flex items-baseline gap-3 mb-2">
            <span style={{ fontFamily: "'Newsreader',serif", fontSize: 52, fontWeight: 500, color: "var(--ink)", lineHeight: 1, letterSpacing: "-0.02em" }}>{sc.overall}</span>
            <span className="text-xl text-slate-400">/ 10</span>
          </div>
        )}
        {sc?.verdict && <p className="text-sm leading-relaxed text-slate-600">{sc.verdict}</p>}
      </div>

      {/* Dimension bars — always visible */}
      {sc && sc.dimensions.length > 0 && (
        <Card className="mb-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400 mb-4">How you scored</p>
          <div className="space-y-3">
            {sc.dimensions.map((d) => (
              <div key={d.name}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="font-semibold text-ink-3">{d.name}</span>
                  <span className="font-bold" style={{ color: barColour(d.pct) }}>{d.pct}%</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-paper-3">
                  <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: "linear-gradient(90deg,var(--accent),#9B8AF0)" }} />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {sc?.html && (
        <Card className="sc-body mb-5">
          <div className="prose-scorecard" dangerouslySetInnerHTML={{ __html: sc.html }} />
        </Card>
      )}

      <p className="mb-2 text-center text-xs text-slate-500">This score is AI-generated practice feedback. It is not a hiring decision. To ask for a human review, contact your consultant.</p>
      <p className="mb-5 text-center text-xs text-slate-400">Shared with your consultant and available to you here.</p>

      <div className="flex flex-col gap-3">
        {/* On a review-only invite /start answers 410, so this button could only
            fail. The scorecard itself stays fully readable, which is the whole
            point of review mode. Codex review, 2026-09-02. */}
        {!invite?.reviewOnly && (
          <Button variant="primary" className="w-full" onClick={practiseAgain}>Practise again →</Button>
        )}
        <Button variant="ghost" className="w-full" onClick={() => navigate(`/invite/${token}/home`)}>Back to home</Button>
      </div>
    </CandidateFlow>
  );
}
